import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAPI } from "../contexts/APIContext";
import logger from "../utils/logger";

/*
 * Cancel flow (web). Same steps as the iOS app:
 *   made     sad Pip + a carousel of everything the family has made
 *   reason   why they're leaving (one tap, optional note)
 *   then a save attempt made for their reason:
 *     too expensive   Family plans: switch to Pro (downgrade / switched)
 *     not using it    tryit: little things to try
 *     lost interest   tryit: something new they haven't seen
 *     not working     support: contact the team
 *   offer    "your next month is on us" (paying subscribers, once a year) —
 *            every reason ends here if they still want to go
 *   confirm  what stays saved vs what stops, then one tap to cancel
 *   saved / done
 * "Continue to cancel" is on every screen — the save offer never hides the exit.
 * Backend: routers/cancel_flow_router.py.
 */

const ART = "/assets/cancel";
const BANNER_ART = "/assets/banner";
const INK = "#0B1A33";
const SLATE = "#5A6B85";
const BLUE = "#0065F8";
const BLUE_DEEP = "#073B8E";
const GOLD = "#FFC53D";
const FONT = "'Fredoka', sans-serif";

const REASONS = [
  { id: "too_expensive", icon: "payments", label: "It's too expensive" },
  { id: "not_using", icon: "schedule", label: "We don't use it enough" },
  { id: "lost_interest", icon: "child_care", label: "My child lost interest" },
  { id: "not_working", icon: "build", label: "Something isn't working" },
  { id: "other", icon: "more_horiz", label: "Something else" },
];

const listText = (parts) =>
  parts.length < 2 ? parts.join("") : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
const fmtDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: "long", day: "numeric" });
};

/* Scattered confetti: fixed pseudo-random pieces (same every time, no reflow). */
const CONFETTI = Array.from({ length: 26 }, (_, i) => {
  const r = (k) => {
    const x = Math.sin(i * 12.9898 + k * 78.233) * 43758.5453;
    return x - Math.floor(x);
  };
  return {
    x: Math.round(r(1) * 96 + 2),
    w: 7 + Math.round(r(2) * 5),
    delay: Math.round(r(3) * 700),
    dur: 1900 + Math.round(r(4) * 1300),
    spin: (r(5) > 0.5 ? 1 : -1) * (360 + Math.round(r(6) * 360)),
    color: [GOLD, BLUE, "#FF7AB0", "#3BE0A0", "#7CC4FF", "#B89BFF"][i % 6],
  };
});

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

const detail = (r, fallback) => r?.detail || r?.error || r?.message || fallback;

function Icon({ name, size = 20, color, fill = true }) {
  return (
    <span
      className="material-symbols-outlined"
      aria-hidden="true"
      style={{ fontSize: size, color, fontVariationSettings: `'FILL' ${fill ? 1 : 0}` }}
    >
      {name}
    </span>
  );
}

/* Two rows of their own creations drifting in opposite directions. */
/** "$14.99/month" → a big "$14.99" over "per month". */
function PriceTile({ plan, price, stars, pro, ribbon }) {
  const [amount, per] = (price || "").split("/");
  return (
    <div className={`cf-price${pro ? " cf-price-pro" : ""}`}>
      {ribbon && <span className="cf-ribbon">{ribbon}</span>}
      <span className="cf-price-plan">{plan}</span>
      <span className="cf-price-amount">{amount}</span>
      {per && <span className="cf-price-per">per {per}</span>}
      <span className="cf-price-stars"><Icon name="star" size={15} color={GOLD} /> {stars} a week</span>
    </div>
  );
}

/** One row of the family's creations. A short row sits still and centred.
    A row wider than the card drifts on its own (doubled, so the loop is
    seamless) and can be dragged or swiped with a trackpad; the drift picks
    back up a moment after you let go, and pauses while the mouse is over it. */
function MadeRow({ items, direction, speed, render }) {
  const ref = useRef(null);
  const trackRef = useRef(null);
  const [overflows, setOverflows] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    // Width of one copy of the (fixed-size) tiles — the same whether the row
    // is still or doubled, so measuring never flips the answer.
    const measure = () => {
      const kids = el.firstElementChild?.children;
      if (!kids || !kids.length) return;
      const first = kids[0], last = kids[Math.min(items.length, kids.length) - 1];
      const one = last.offsetLeft + last.offsetWidth - first.offsetLeft;
      const next = one > el.clientWidth - 24;
      setOverflows((prev) => (prev === next ? prev : next));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [items.length]);

  useEffect(() => {
    const row = ref.current, track = trackRef.current;
    if (!overflows || !row || !track) return undefined;
    const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const sign = direction === "left" ? 1 : -1;   // +1: content drifts left
    let pos = 0, last = performance.now(), resumeAt = 0, hover = false, frame = 0;
    let dragging = false, dragX = 0, moved = 0;
    const loop = () => {
      const kids = track.children;
      return kids[items.length] ? kids[items.length].offsetLeft - kids[0].offsetLeft : track.scrollWidth / 2;
    };
    const paint = () => {
      const L = loop();
      pos = ((pos % L) + L) % L;
      track.style.transform = `translate3d(${sign > 0 ? -pos : pos - L}px,0,0)`;
    };
    const tick = (now) => {
      const dt = Math.min(now - last, 64) / 1000;
      last = now;
      if (!still && !dragging && !hover && now > resumeAt) pos += speed * dt;
      paint();
      frame = requestAnimationFrame(tick);
    };
    // Moving the content by dx pixels (finger/trackpad direction).
    const shift = (dx) => { pos -= sign * dx; resumeAt = performance.now() + 1200; };
    const down = (e) => {
      if (e.pointerType === "mouse" && e.button !== 0) return;
      dragging = true; dragX = e.clientX; moved = 0;
      row.setPointerCapture(e.pointerId);
      row.classList.add("cf-grabbing");
    };
    const move = (e) => {
      if (!dragging) return;
      const dx = e.clientX - dragX;
      dragX = e.clientX; moved += Math.abs(dx);
      shift(dx);
    };
    const up = (e) => {
      if (!dragging) return;
      dragging = false;
      row.classList.remove("cf-grabbing");
      if (row.hasPointerCapture(e.pointerId)) row.releasePointerCapture(e.pointerId);
    };
    const wheel = (e) => {
      // Only sideways swipes; vertical wheel keeps scrolling the page.
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
      e.preventDefault();
      shift(-e.deltaX);
    };
    const enter = (e) => { if (e.pointerType === "mouse") hover = true; };
    const leave = () => { hover = false; };
    row.addEventListener("pointerdown", down);
    row.addEventListener("pointermove", move);
    row.addEventListener("pointerup", up);
    row.addEventListener("pointercancel", up);
    row.addEventListener("pointerenter", enter);
    row.addEventListener("pointerleave", leave);
    row.addEventListener("wheel", wheel, { passive: false });
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      row.removeEventListener("pointerdown", down);
      row.removeEventListener("pointermove", move);
      row.removeEventListener("pointerup", up);
      row.removeEventListener("pointercancel", up);
      row.removeEventListener("pointerenter", enter);
      row.removeEventListener("pointerleave", leave);
      row.removeEventListener("wheel", wheel);
      track.style.transform = "";
    };
  }, [overflows, direction, speed, items.length]);

  const list = overflows ? [...items, ...items] : items;
  return (
    <div className={`cf-row${overflows ? " cf-row-live" : ""}`} ref={ref}>
      <div className={overflows ? "cf-track" : "cf-track cf-still"} ref={trackRef}>
        {list.map((item, i) => render(item, i, i >= items.length))}
      </div>
    </div>
  );
}

function MadeCarousel({ stories, characters }) {
  if (!stories.length && !characters.length) return null;
  return (
    <div className="cf-carousel" aria-label="Things your family made">
      {stories.length > 0 && (
        <MadeRow items={stories} direction="left" speed={34} render={(s, i, dup) => (
          <div className="cf-story" key={`${s.id}-${i}`} aria-hidden={dup}>
            <img src={s.image_url} alt="" loading="lazy" />
            <span>{s.name}</span>
          </div>
        )} />
      )}
      {characters.length > 0 && (
        <MadeRow items={characters} direction="right" speed={26} render={(c, i, dup) => (
          <div className="cf-char" key={`${c.id}-${i}`} aria-hidden={dup}>
            <div className="cf-char-img"><img src={c.image_url} alt="" loading="lazy" /></div>
            <span>{c.name}</span>
          </div>
        )} />
      )}
    </div>
  );
}

/** Their own story covers fanned out like keepsakes (characters if no stories). */
function KeepsakeFan({ stories, characters }) {
  const wide = stories.length > 0;
  const items = (wide ? stories : characters).slice(0, 3);
  if (!items.length) return <img className="cf-pip cf-sway" src={`${ART}/pip_sad.webp`} alt="Pip looking sad" width="150" />;
  const mid = (items.length - 1) / 2;
  return (
    <div className={`cf-fan${wide ? "" : " cf-fan-sq"}`} aria-hidden="true">
      {items.map((it, i) => {
        const k = items.length === 1 ? 0 : i - mid;
        return (
          <img key={it.id} src={it.image_url} alt=""
            style={{ transform: `translateX(calc(-50% + ${k * (wide ? 84 : 70)}px)) translateY(${Math.abs(k) * 10}px) rotate(${k * 9}deg)`, zIndex: k === 0 ? 2 : 1 }} />
        );
      })}
      <span className="cf-saved"><Icon name="favorite" size={14} color="#FF5C7A" /> Saved</span>
    </div>
  );
}

export default function CancelFlow({ isOpen, onClose, onChanged, client }) {
  const { apiClient: contextClient } = useAPI();
  // `client` lets the dev-only preview page drive the flow with sample data.
  const apiClient = client || contextClient;
  const [step, setStep] = useState("loading");
  const [state, setState] = useState(null);
  const [reason, setReason] = useState(null);
  const [note, setNote] = useState("");
  const [session, setSession] = useState(null); // { session_id, offer }
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState("");
  const [periodEnd, setPeriodEnd] = useState(null);
  const [switchStarts, setSwitchStarts] = useState(null);
  const finished = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isOpen) return undefined;
    let live = true;
    finished.current = false;
    setStep("loading");
    setReason(null);
    setNote("");
    setSession(null);
    setProblem("");
    apiClient
      .getCancelFlow()
      .then((r) => {
        if (!live) return;
        if (!r || r.status === false || r.detail) throw new Error(detail(r, "We couldn't load your subscription."));
        setState(r);
        setPeriodEnd(r.subscription?.period_end || null);
        setStep("made");
      })
      .catch((e) => {
        if (!live) return;
        logger.error("cancel flow load failed", e);
        setProblem(e.message || "We couldn't load your subscription. Please try again.");
        setStep("error");
      });
    return () => {
      live = false;
    };
  }, [isOpen, apiClient]);

  const close = useCallback(
    (changed = false) => {
      if (session?.session_id && !finished.current) {
        apiClient.cancelFlowOutcome(session.session_id, "abandoned").catch(() => {});
      }
      onClose?.();
      if (changed) onChanged?.();
    },
    [apiClient, onChanged, onClose, session]
  );

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => e.key === "Escape" && !busy && close(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, busy, close]);

  const made = state?.made;
  const counts = useMemo(() => made?.counts || { stories: 0, characters: 0, storybooks: 0 }, [made]);
  const endDate = fmtDate(periodEnd);
  const madeNothing = !counts.stories && !counts.characters && !counts.storybooks;

  const submitReason = async () => {
    if (!reason || busy) return;
    setBusy(true);
    setProblem("");
    try {
      const r = await apiClient.startCancelFlow(reason, reason === "other" || note ? note : null);
      if (!r?.session_id) throw new Error(detail(r, "Something went wrong. Please try again."));
      setSession(r);
      if (reason === "too_expensive" && r.downgrade) setStep("downgrade");
      else if (reason === "not_using" || reason === "lost_interest") setStep("tryit");
      else if (reason === "not_working") setStep("support");
      else if (r.offer?.type === "free_month") setStep("offer");
      else setStep("confirm");
    } catch (e) {
      setProblem(e.message);
    } finally {
      setBusy(false);
    }
  };

  const claim = async () => {
    if (busy) return;
    setBusy(true);
    setProblem("");
    try {
      const r = await apiClient.acceptCancelOffer(session.session_id);
      if (!r || !(r.status === "applied" || r.status === "already_applied")) {
        throw new Error(detail(r, "We couldn't apply your free month. Please try again."));
      }
      finished.current = true;
      setStep("saved");
    } catch (e) {
      setProblem(e.message);
    } finally {
      setBusy(false);
    }
  };

  const confirmCancel = async () => {
    if (busy) return;
    setBusy(true);
    setProblem("");
    try {
      let sid = session?.session_id;
      if (!sid) {
        // Straight from "what you made" → confirm without a reason: record "other".
        const r = await apiClient.startCancelFlow(reason || "other", note || null);
        if (!r?.session_id) throw new Error(detail(r, "Something went wrong. Please try again."));
        sid = r.session_id;
        setSession(r);
      }
      const r = await apiClient.confirmCancelFlow(sid);
      if (!r || !["cancelled", "no_subscription"].includes(r.status)) {
        throw new Error(detail(r, "We couldn't cancel right now. Please try again."));
      }
      if (r.period_end) setPeriodEnd(r.period_end);
      finished.current = true;
      setStep("done");
    } catch (e) {
      setProblem(e.message);
    } finally {
      setBusy(false);
    }
  };

  const keep = () => close(false);

  // After a reason's own save attempt: the free month if they have one, else confirm.
  const offerOrConfirm = () => setStep(session?.offer?.type === "free_month" ? "offer" : "confirm");

  const switchToPro = async () => {
    if (busy) return;
    setBusy(true);
    setProblem("");
    try {
      const r = await apiClient.acceptCancelDowngrade(session.session_id);
      if (r?.status === "portal" && r.portal_url) {
        finished.current = true;
        window.location.assign(r.portal_url);
        return;
      }
      if (r?.status !== "scheduled") throw new Error(detail(r, "We couldn't switch your plan. Please try again."));
      finished.current = true;
      setSwitchStarts(r.starts_at || periodEnd);
      setStep("switched");
    } catch (e) {
      setProblem(e.message);
    } finally {
      setBusy(false);
    }
  };

  const tryThing = (path) => {
    finished.current = true;
    if (session?.session_id) apiClient.cancelFlowOutcome(session.session_id, "engaged").catch(() => {});
    onClose?.();
    navigate(path);
  };

  const madeLines = useMemo(() => {
    const parts = [];
    if (counts.stories) parts.push(plural(counts.stories, "story", "stories"));
    if (counts.characters) parts.push(plural(counts.characters, "character", "characters"));
    if (counts.storybooks) parts.push(plural(counts.storybooks, "storybook", "storybooks"));
    return parts;
  }, [counts]);

  if (!isOpen) return null;

  const wide = step === "made";

  return (
    <div className="cf-overlay" role="dialog" aria-modal="true" aria-label="Cancel subscription" onClick={() => !busy && close(false)}>
      <style>{CSS}</style>
      <div className={`cf-card${wide ? " cf-wide" : ""}`} onClick={(e) => e.stopPropagation()}>
        <button className="cf-x" onClick={() => close(false)} disabled={busy} aria-label="Close">
          <Icon name="close" size={20} color={SLATE} />
        </button>

        {step === "loading" && (
          <div className="cf-center cf-pad">
            <img className="cf-pip cf-bob" src={`${ART}/pip_thinking.webp`} alt="" width="130" />
            <p className="cf-sub">One moment…</p>
          </div>
        )}

        {step === "error" && (
          <div className="cf-center cf-pad">
            <img className="cf-pip" src={`${ART}/pip_fix.webp`} alt="" width="150" />
            <h2 className="cf-title">Hmm, that didn't load</h2>
            <p className="cf-sub">{problem}</p>
            <div className="cf-actions">
              <button className="cf-btn cf-primary" onClick={() => close(false)}>Close</button>
            </div>
          </div>
        )}

        {step === "made" && (
          <div className="cf-center cf-enter">
            <div className="cf-stage">
              <img className="cf-pip cf-sway" src={`${ART}/pip_sad.webp`} alt="Pip looking sad" width="190" />
            </div>
            <div className="cf-pad cf-tight">
              <h2 className="cf-title">
                {madeNothing ? "Pip was just getting started!"
                  : counts.stories + counts.characters + counts.storybooks <= 2 ? "Wait… look at what you've made!"
                  : "Wait… look at everything you've made!"}
              </h2>
              {madeLines.length > 0 ? (
                <div className="cf-chips">
                  {madeLines.map((t) => (
                    <span className="cf-chip" key={t}>{t}</span>
                  ))}
                </div>
              ) : (
                <p className="cf-sub">You haven't made a story together yet. Your first one only takes a minute.</p>
              )}
            </div>
            <MadeCarousel stories={made?.stories || []} characters={made?.characters || []} />
            <div className="cf-pad cf-actions">
              {madeNothing
                ? <button className="cf-btn cf-primary" onClick={() => tryThing("/create-story")}><Icon name="auto_awesome" size={20} color="#fff" /> Make our first story</button>
                : <button className="cf-btn cf-primary" onClick={keep}>Keep my subscription</button>}
              <button className="cf-btn cf-ghost" onClick={() => setStep("reason")}>Continue to cancel</button>
            </div>
          </div>
        )}

        {step === "reason" && (
          <div className="cf-pad cf-enter">
            <div className="cf-head">
              <img className="cf-pip-sm cf-bob" src={`${ART}/pip_thinking.webp`} alt="" width="92" />
              <div>
                <h2 className="cf-title cf-left">Can you tell us why?</h2>
                <p className="cf-sub cf-left">It helps us make Pixley better for every family.</p>
              </div>
            </div>
            <div className="cf-reasons" role="radiogroup" aria-label="Reason for cancelling">
              {REASONS.map((r) => (
                <button
                  key={r.id}
                  role="radio"
                  aria-checked={reason === r.id}
                  className={`cf-reason${reason === r.id ? " on" : ""}`}
                  onClick={() => setReason(r.id)}
                >
                  <span className="cf-reason-icon"><Icon name={r.icon} size={20} color={reason === r.id ? "#fff" : BLUE} /></span>
                  {r.label}
                  <span className="cf-radio" />
                </button>
              ))}
            </div>
            {reason && (
              <textarea
                className="cf-note"
                rows={2}
                maxLength={500}
                placeholder={reason === "other" ? "Tell us a little more (optional)" : "Anything else you'd like to share? (optional)"}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            )}
            {problem && <p className="cf-problem" role="alert">{problem}</p>}
            <div className="cf-actions">
              <button className={`cf-btn cf-primary${!reason ? " cf-waiting" : ""}`} disabled={!reason || busy} onClick={submitReason}>
                {busy ? "One moment…" : "Continue"}
              </button>
              <button className="cf-btn cf-ghost" onClick={keep} disabled={busy}>Keep my subscription</button>
            </div>
          </div>
        )}

        {step === "support" && (
          <div className="cf-center cf-pad cf-enter">
            <img className="cf-pip cf-bob" src={`${ART}/pip_fix.webp`} alt="Pip with a wrench" width="150" />
            <h2 className="cf-title">Let us fix it!</h2>
            <p className="cf-sub">
              Tell us what went wrong and we'll help sort it out. Include what you were doing when it happened.
            </p>
            <div className="cf-actions">
              <a
                className="cf-btn cf-primary"
                href={`mailto:${state?.support_email || "team@pixleyai.com"}?subject=${encodeURIComponent("Something isn't working in Pixley")}`}
                onClick={() => {
                  finished.current = true;
                  if (session?.session_id) apiClient.cancelFlowOutcome(session.session_id, "support").catch(() => {});
                }}
              >
                <Icon name="mail" size={18} color="#fff" /> Email the Pixley team
              </a>
              <button className="cf-btn cf-ghost" onClick={offerOrConfirm}>
                Continue to cancel
              </button>
            </div>
          </div>
        )}

        {step === "offer" && (
          <div className="cf-center cf-enter">
            <div className="cf-stage cf-gift-stage">
              <div className="cf-rays" aria-hidden="true" />
              <img className="cf-pip cf-hop" src={`${ART}/pip_gift.webp`} alt="Pip holding a gift" width="190" />
            </div>
            <div className="cf-pad cf-tight">
              <span className="cf-badge"><Icon name="redeem" size={16} color={INK} /> A gift from Pip</span>
              <h2 className="cf-title cf-big">{reason === "not_working" ? "Sorry for the trouble" : "Your next month is on us"}</h2>
              <p className="cf-sub">
                {reason === "not_working" ? "While we sort it out, your next month is on us" : "Stay with Pixley and your next month is completely free"}
                {endDate ? `, with no charge on ${endDate}` : ""}.
                After that it's your usual plan, and you can still cancel anytime.
              </p>
              {problem && <p className="cf-problem" role="alert">{problem}</p>}
              <div className="cf-actions">
                <button className="cf-btn cf-gold" onClick={claim} disabled={busy}>
                  {busy ? "Adding your free month…" : "Claim my free month"}
                </button>
                <button className="cf-btn cf-ghost" onClick={() => setStep("confirm")} disabled={busy}>
                  No thanks, continue to cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {step === "downgrade" && session?.downgrade && (
          <div className="cf-center cf-pad cf-enter">
            <img className="cf-pip cf-pip-mid cf-bob" src={`${ART}/pip_piggy.webp`} alt="Pip hugging a piggy bank" width="170" />
            <h2 className="cf-title cf-big">Switch to Pro instead?</h2>
            <p className="cf-sub">
              Keep everything you've made, for {session.downgrade.saves.replace(/^(\S+) /, "$1 less ")}.
            </p>
            <div className="cf-prices">
              <PriceTile plan="Family" price={session.downgrade.from_price_display} stars="650" />
              <PriceTile plan="Pro" price={session.downgrade.price_display} stars="250" pro
                ribbon={`Save ${session.downgrade.saves.replace(" a month", "/mo").replace(" a year", "/yr")}`} />
            </div>
            <p className="cf-fine">
              You keep Family{endDate ? ` until ${endDate}` : " until your next renewal"}, then Pro starts. Switch back anytime.
            </p>
            {problem && <p className="cf-problem" role="alert">{problem}</p>}
            <div className="cf-actions">
              <button className="cf-btn cf-primary" onClick={switchToPro} disabled={busy}>
                {busy ? "Switching…" : session.downgrade.via === "portal" ? "Switch to Pro on my billing page" : "Switch to Pro"}
              </button>
              <button className="cf-btn cf-ghost" onClick={offerOrConfirm} disabled={busy}>No thanks, continue to cancel</button>
            </div>
          </div>
        )}

        {step === "switched" && (
          <div className="cf-center cf-pad cf-enter">
            <img className="cf-pip cf-hop" src={`${ART}/pip_heart.webp`} alt="Pip hugging a heart" width="160" />
            <h2 className="cf-title cf-big">You're switching to Pro</h2>
            <p className="cf-sub">
              You'll stay on Family{fmtDate(switchStarts) ? ` until ${fmtDate(switchStarts)}` : " until your next renewal"}, then Pro starts at {session?.downgrade?.price_display}. Nothing else changes.
            </p>
            <div className="cf-actions">
              <button className="cf-btn cf-primary" onClick={() => close(true)}>Back to Pixley</button>
            </div>
          </div>
        )}

        {step === "tryit" && (
          <div className="cf-center cf-pad cf-enter">
            <img className="cf-pip cf-pip-sm2 cf-bob" src={`${ART}/pip_idea.webp`} alt="" width="120" />
            <h2 className="cf-title cf-big">{reason === "lost_interest" ? "Something new to try" : "Pixley fits in little moments"}</h2>
            <p className="cf-sub">
              {reason === "lost_interest"
                ? "We add new shows and ways to play all the time. Pick one for tonight:"
                : "A story at bedtime or a quick lesson on the go:"}
            </p>
            <div className="cf-try">
              {state?.newest_original && (
                <button className="cf-feature" onClick={() => tryThing(`/storyverse/shows/${state.newest_original.id}`)}>
                  {state.newest_original.image_url && <img src={state.newest_original.image_url} alt="" />}
                  <span className="cf-feature-text">
                    <small>New Pixley Original</small>
                    <strong>{state.newest_original.name}</strong>
                  </span>
                  <span className="cf-feature-play"><Icon name="play_arrow" size={26} color={BLUE} /></span>
                </button>
              )}
              <div className="cf-tiles">
                {(reason === "lost_interest"
                  ? [
                      { path: "/download", art: `${BANNER_ART}/promo_interactive.webp`, label: "An interactive story", sub: "They choose what happens next, in the Pixley app" },
                      { path: "/learn", art: `${ART}/try_learn.webp`, label: "Learn with Pip", sub: "Letters, counting and writing games" },
                    ]
                  : [
                      { path: "/learn", art: `${ART}/try_learn.webp`, label: "A 5-minute lesson", sub: "Letters, counting and writing games" },
                      { path: "/create-story", art: `${ART}/try_classic.webp`, label: "A bedtime story", sub: "Pick a topic, Pixley does the rest" },
                    ]
                ).map((t) => (
                  <button key={t.path} className="cf-tile" onClick={() => tryThing(t.path)}>
                    <img src={t.art} alt="" />
                    <span className="cf-tile-text">
                      <strong>{t.label}</strong>
                      <small>{t.sub}</small>
                    </span>
                  </button>
                ))}
              </div>
            </div>
            <div className="cf-actions">
              <button className="cf-btn cf-primary" onClick={keep}>Keep my subscription</button>
              <button className="cf-btn cf-ghost" onClick={offerOrConfirm}>Continue to cancel</button>
            </div>
          </div>
        )}

        {step === "saved" && (
          <div className="cf-center cf-pad cf-enter">
            <div className="cf-confetti" aria-hidden="true">
              {CONFETTI.map((c, i) => (
                <i
                  key={i}
                  style={{
                    left: `${c.x}%`,
                    width: c.w,
                    height: c.w * 1.5,
                    background: c.color,
                    animationDelay: `${c.delay}ms`,
                    animationDuration: `${c.dur}ms`,
                    "--spin": `${c.spin}deg`,
                  }}
                />
              ))}
            </div>
            <img className="cf-pip cf-hop" src={`${ART}/pip_heart.webp`} alt="Pip hugging a heart" width="170" />
            <h2 className="cf-title cf-big">Yay! Your next month is free</h2>
            <p className="cf-sub">Pip is so happy you're staying. We've added a free month to your plan, so there's nothing to pay next time.</p>
            <div className="cf-actions">
              <button className="cf-btn cf-primary" onClick={() => close(true)}>Back to Pixley</button>
            </div>
          </div>
        )}

        {step === "confirm" && (
          <div className="cf-center cf-pad cf-enter">
            <KeepsakeFan stories={made?.stories || []} characters={made?.characters || []} />
            <h2 className="cf-title cf-big">Cancel your subscription?</h2>
            <p className="cf-sub">{madeLines.length
              ? `Your ${listText(madeLines)} ${counts.stories + counts.characters + counts.storybooks === 1 ? "stays" : "stay"} saved.`
              : "Here's what happens next."}</p>
            <ol className="cf-timeline">
              <li style={{ "--dot": BLUE }}>
                <small>{endDate ? `Until ${endDate}` : "Until your plan ends"}</small>
                <span>Everything works just like now</span>
              </li>
              <li style={{ "--dot": "#94A3B8" }}>
                <small>{endDate || "When it ends"}</small>
                <span>No new stories, characters or weekly stars</span>
              </li>
              <li style={{ "--dot": GOLD }}>
                <small>Anytime</small>
                <span>{madeLines.length ? "Come back and it's all still here" : "Come back and pick up where you left off"}</span>
              </li>
            </ol>
            {problem && <p className="cf-problem" role="alert">{problem}</p>}
            <div className="cf-actions">
              <button className="cf-btn cf-primary" onClick={keep} disabled={busy}>Keep my subscription</button>
              <button className="cf-btn cf-outline" onClick={confirmCancel} disabled={busy}>
                {busy ? "Cancelling…" : "Cancel subscription"}
              </button>
            </div>
          </div>
        )}

        {step === "done" && (
          <div className="cf-center cf-pad cf-enter">
            <img className="cf-pip cf-wave" src={`${ART}/pip_bye.webp`} alt="Pip waving goodbye" width="160" />
            <h2 className="cf-title">You're all set</h2>
            <p className="cf-sub">
              Your subscription won't renew. {endDate ? `You can keep enjoying Pixley until ${endDate}, ` : "You can keep enjoying Pixley until the end of this period, "}
              and everything you made stays saved. We'll miss you!
            </p>
            <div className="cf-actions">
              <button className="cf-btn cf-primary" onClick={() => close(true)}>Back to Pixley</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const CSS = `
.cf-overlay{position:fixed;inset:0;z-index:120;display:flex;align-items:center;justify-content:center;padding:16px;
  background:rgba(11,26,51,.46);backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px);animation:cf-fade .25s ease both}
.cf-card{position:relative;width:min(640px,100%);max-height:calc(100vh - 32px);overflow-y:auto;overflow-x:hidden;border-radius:30px;
  background:linear-gradient(180deg,#F5FAFF 0%,#EAF3FF 100%);border:2px solid rgba(11,26,51,.06);
  box-shadow:0 6px 0 rgba(11,26,51,.10),0 30px 80px rgba(11,26,51,.28);font-family:${FONT};color:${INK};animation:cf-pop .38s cubic-bezier(.2,1.2,.4,1) both}
.cf-card.cf-wide{width:min(780px,100%)}
.cf-x{position:absolute;top:14px;right:14px;z-index:3;width:38px;height:38px;border-radius:50%;border:2px solid rgba(11,26,51,.08);
  background:#fff;display:flex;align-items:center;justify-content:center;cursor:pointer}
.cf-pad{padding:36px 44px 34px}
.cf-tight{padding-top:4px;padding-bottom:0}
.cf-center{text-align:center;display:flex;flex-direction:column;align-items:center}
.cf-stage{position:relative;width:100%;display:flex;justify-content:center;padding-top:26px;
  background:radial-gradient(60% 80% at 50% 100%,rgba(175,227,254,.75) 0%,rgba(175,227,254,0) 70%)}
.cf-pip{display:block;height:auto;filter:drop-shadow(0 10px 18px rgba(11,26,51,.14))}
.cf-pip-sm{flex-shrink:0;height:auto;filter:drop-shadow(0 6px 12px rgba(11,26,51,.12))}
.cf-title{margin:12px 0 8px;font-weight:700;font-size:31px;line-height:1.12;letter-spacing:-.01em;text-wrap:balance}
.cf-title.cf-big{font-size:36px}
.cf-card .cf-pip{width:210px}
.cf-stage .cf-pip{width:180px}
.cf-card .cf-pip-sm{width:112px}
.cf-title.cf-left,.cf-sub.cf-left{text-align:left;margin-left:0}
.cf-sub{margin:0 auto;max-width:46ch;color:${SLATE};font-size:17px;line-height:1.5}
.cf-head{display:flex;align-items:center;gap:16px;margin:4px 34px 18px 0}
.cf-chips{display:flex;flex-wrap:wrap;justify-content:center;gap:8px;margin-top:10px}
.cf-chip{padding:7px 14px;border-radius:999px;background:#fff;border:2px solid rgba(0,101,248,.14);color:${BLUE};font-weight:700;font-size:14px;
  box-shadow:0 2px 0 rgba(11,26,51,.05)}
.cf-carousel{width:100%;display:flex;flex-direction:column;gap:12px;padding:20px 0 4px;
  -webkit-mask-image:linear-gradient(90deg,transparent,#000 9%,#000 91%,transparent);mask-image:linear-gradient(90deg,transparent,#000 9%,#000 91%,transparent)}
.cf-row{overflow:hidden}
.cf-track.cf-still{width:auto;justify-content:center}
.cf-row-live{cursor:grab;touch-action:pan-y;user-select:none;-webkit-user-select:none}
.cf-row-live.cf-grabbing{cursor:grabbing}
.cf-row-live img{pointer-events:none;-webkit-user-drag:none}
.cf-track{display:flex;gap:12px;width:max-content;will-change:transform}
.cf-story{position:relative;width:212px;height:124px;border-radius:16px;overflow:hidden;flex-shrink:0;border:3px solid #fff;
  box-shadow:0 4px 0 rgba(11,26,51,.08),0 10px 22px rgba(11,26,51,.12);background:#dbe8f8}
.cf-story img{width:100%;height:100%;object-fit:cover;display:block}
.cf-story span{position:absolute;inset:auto 0 0;padding:18px 10px 7px;font-size:12.5px;font-weight:700;color:#fff;text-align:left;
  background:linear-gradient(0deg,rgba(11,26,51,.78),rgba(11,26,51,0));white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.cf-char{width:112px;flex-shrink:0;display:flex;flex-direction:column;align-items:center;gap:6px}
.cf-char-img{width:102px;height:102px;border-radius:24px;overflow:hidden;background:#fff;border:3px solid #fff;
  box-shadow:0 4px 0 rgba(11,26,51,.08),0 8px 18px rgba(11,26,51,.10)}
.cf-char-img img{width:100%;height:100%;object-fit:cover;display:block}
.cf-char span{font-size:12.5px;font-weight:700;color:${INK};max-width:92px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.cf-actions{display:flex;flex-direction:column;gap:10px;width:100%;margin-top:26px}
.cf-center .cf-actions{max-width:440px;margin-left:auto;margin-right:auto}
.cf-row-actions{flex-direction:row-reverse}
.cf-row-actions .cf-btn{flex:1}
.cf-btn{transition:background .18s,color .18s,box-shadow .18s,transform .18s;display:inline-flex;align-items:center;justify-content:center;gap:8px;min-height:56px;padding:0 24px;border-radius:999px;
  font-family:${FONT};font-weight:700;font-size:16px;cursor:pointer;text-decoration:none;transition:transform .12s ease,box-shadow .12s ease;border:none}
.cf-btn:active:not(:disabled){transform:translateY(3px)}
.cf-btn:disabled{opacity:.55;cursor:default}
/* A primary waiting on a choice: flat and quiet, not a washed-out blue on its lip. */
.cf-primary.cf-waiting:disabled{opacity:1;background:rgba(11,26,51,.07);color:rgba(90,107,133,.75);box-shadow:none;transform:translateY(4px)}
.cf-btn:focus-visible,.cf-reason:focus-visible,.cf-x:focus-visible{outline:3px solid ${GOLD};outline-offset:3px}
.cf-primary{background:${BLUE};color:#fff;box-shadow:0 4px 0 ${BLUE_DEEP},0 12px 26px rgba(0,101,248,.22)}
.cf-gold{background:${GOLD};color:${INK};box-shadow:0 4px 0 #E89410,0 12px 26px rgba(255,197,61,.35)}
.cf-ghost{background:transparent;color:${SLATE};min-height:44px;font-size:15px}
.cf-ghost:hover{color:${INK}}
.cf-outline{background:#fff;color:${INK};border:2px solid rgba(11,26,51,.14);box-shadow:0 3px 0 rgba(11,26,51,.06)}
.cf-reasons{display:flex;flex-direction:column;gap:9px}
.cf-reason{display:flex;align-items:center;gap:14px;width:100%;padding:14px 16px;border-radius:18px;background:#fff;text-align:left;
  border:2px solid rgba(11,26,51,.08);box-shadow:0 2px 0 rgba(11,26,51,.04);font-family:${FONT};font-size:16px;font-weight:600;color:${INK};cursor:pointer;
  transition:border-color .15s,background .15s,transform .12s}
.cf-reason:hover{border-color:rgba(0,101,248,.35)}
.cf-reason.on{border-color:${BLUE};background:#F0F6FF}
.cf-reason-icon{width:36px;height:36px;border-radius:12px;background:rgba(0,101,248,.09);display:flex;align-items:center;justify-content:center;flex-shrink:0;transition:background .15s}
.cf-reason.on .cf-reason-icon{background:${BLUE}}
.cf-radio{margin-left:auto;width:22px;height:22px;border-radius:50%;border:2px solid rgba(11,26,51,.18);flex-shrink:0;transition:all .15s}
.cf-reason.on .cf-radio{border:7px solid ${BLUE}}
.cf-note{width:100%;margin-top:10px;padding:12px 14px;border-radius:16px;border:2px solid rgba(11,26,51,.10);font-family:${FONT};font-size:15px;
  color:${INK};background:#fff;resize:none;outline:none;animation:cf-rise .25s ease both}
.cf-note:focus{border-color:${BLUE}}
.cf-problem{margin:12px 0 0;color:#D12F4A;font-size:14px;font-weight:600;text-align:center}
.cf-badge{display:inline-flex;align-items:center;gap:6px;padding:6px 14px;border-radius:999px;background:${GOLD};color:${INK};font-size:13px;
  font-weight:700;letter-spacing:.06em;text-transform:uppercase;box-shadow:0 3px 0 #E89410}
.cf-badge-blue{background:${BLUE};color:#fff;box-shadow:0 3px 0 ${BLUE_DEEP}}}};background:#F0F6FF}}
.cf-fine{margin:12px auto 0;color:${SLATE};font-size:13.5px}
.cf-try{display:flex;flex-direction:column;gap:14px;width:100%;margin-top:22px}
.cf-feature{position:relative;display:flex;align-items:flex-end;gap:12px;width:100%;height:172px;padding:16px;border-radius:24px;overflow:hidden;
  border:3px solid #fff;background:linear-gradient(${BLUE},${BLUE_DEEP});cursor:pointer;text-align:left;font-family:inherit;
  box-shadow:0 10px 26px rgba(11,26,51,.14);transition:transform .15s}
.cf-feature img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover}
.cf-feature::after{content:"";position:absolute;inset:0;background:linear-gradient(180deg,transparent 40%,rgba(0,0,0,.7))}
.cf-feature-text{position:relative;z-index:1;display:flex;flex-direction:column;align-items:flex-start;gap:5px;flex:1;min-width:0}
.cf-feature-text small{padding:3px 9px;border-radius:999px;background:${GOLD};color:${INK};font-size:11.5px;font-weight:800;letter-spacing:.08em;text-transform:uppercase}
.cf-feature-text strong{color:#fff;font-size:23px;font-weight:700;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:100%}
.cf-feature-play{position:relative;z-index:1;width:48px;height:48px;border-radius:50%;background:#fff;display:flex;align-items:center;justify-content:center;flex-shrink:0}
.cf-tiles{display:grid;grid-template-columns:1fr 1fr;gap:14px}
.cf-tile{display:flex;flex-direction:column;padding:0;border-radius:22px;overflow:hidden;background:#fff;border:2px solid rgba(11,26,51,.06);
  cursor:pointer;text-align:left;font-family:inherit;box-shadow:0 8px 20px rgba(11,26,51,.08);transition:transform .15s,border-color .15s}
.cf-tile img{width:100%;height:120px;object-fit:cover;display:block}
.cf-tile-text{display:flex;flex-direction:column;gap:3px;padding:12px 14px 14px}
.cf-tile-text strong{font-size:17px;color:${INK};font-weight:700}
.cf-tile-text small{font-size:13.5px;color:${SLATE};font-weight:500;line-height:1.35}
.cf-feature:hover,.cf-tile:hover{transform:translateY(-2px)}
.cf-tile:hover{border-color:rgba(0,101,248,.35)}
.cf-feature:focus-visible,.cf-tile:focus-visible{outline:3px solid ${GOLD};outline-offset:3px}
.cf-pip-mid{width:170px!important}
.cf-pip-sm2{width:100px!important}
.cf-prices{display:grid;grid-template-columns:1fr 1fr;align-items:end;gap:14px;width:100%;max-width:440px;margin:30px auto 0}
.cf-price{position:relative;display:flex;flex-direction:column;align-items:center;padding:18px 12px;border-radius:24px;background:#fff;border:2px solid rgba(11,26,51,.08);opacity:.92}
.cf-price-pro{padding:26px 12px;border:none;opacity:1;background:linear-gradient(${BLUE},${BLUE_DEEP});box-shadow:0 14px 30px rgba(0,101,248,.3)}
.cf-ribbon{position:absolute;top:-13px;left:50%;transform:translateX(-50%);white-space:nowrap;padding:5px 12px;border-radius:999px;background:${GOLD};
  color:${INK};font-size:12px;font-weight:800;letter-spacing:.06em;text-transform:uppercase;box-shadow:0 2px 0 #E89410}
.cf-price-plan{font-size:13px;font-weight:800;letter-spacing:.1em;text-transform:uppercase;color:${SLATE}}
.cf-price-amount{margin-top:6px;font-size:28px;font-weight:700;color:${SLATE};text-decoration:line-through;text-decoration-color:rgba(90,107,133,.7)}
.cf-price-per{font-size:13px;font-weight:600;color:${SLATE}}
.cf-price-stars{display:inline-flex;align-items:center;gap:4px;margin-top:12px;padding:6px 11px;border-radius:999px;background:rgba(11,26,51,.05);font-size:14px;font-weight:700;color:${INK}}
.cf-price-pro .cf-price-plan,.cf-price-pro .cf-price-per{color:rgba(255,255,255,.85)}
.cf-price-pro .cf-price-amount{font-size:38px;color:#fff;text-decoration:none}
.cf-price-pro .cf-price-stars{background:rgba(255,255,255,.16);color:#fff}
.cf-prices+.cf-fine{margin-top:22px;max-width:44ch};transition:border-color .15s,transform .12s};outline-offset:3px};font-weight:500};font-weight:700}
.cf-gift-stage{overflow:hidden;background:radial-gradient(60% 90% at 50% 100%,rgba(255,197,61,.45) 0%,rgba(255,197,61,0) 70%)}
.cf-rays{position:absolute;left:50%;bottom:-170px;width:520px;height:520px;margin-left:-260px;border-radius:50%;opacity:.35;
  background:repeating-conic-gradient(from 0deg,rgba(255,197,61,.9) 0deg 8deg,transparent 8deg 22deg);animation:cf-spin 30s linear infinite;
  -webkit-mask-image:radial-gradient(circle,#000 30%,transparent 70%);mask-image:radial-gradient(circle,#000 30%,transparent 70%)}
.cf-gift-stage .cf-pip{position:relative}
.cf-fan{position:relative;width:100%;height:178px;margin-bottom:14px;
  background:radial-gradient(closest-side,rgba(175,227,254,.7),rgba(175,227,254,0))}
.cf-fan img{position:absolute;left:50%;top:22px;width:176px;height:110px;object-fit:cover;border-radius:16px;border:4px solid #fff;
  box-shadow:0 10px 22px rgba(11,26,51,.16)}
.cf-fan-sq img{width:110px;height:110px;border-radius:24px}
.cf-saved{position:absolute;left:50%;bottom:6px;transform:translateX(-50%);z-index:3;display:inline-flex;align-items:center;gap:5px;
  padding:6px 13px;border-radius:999px;background:#fff;color:${INK};font-size:12px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;
  box-shadow:0 4px 12px rgba(11,26,51,.14)}
.cf-timeline{list-style:none;margin:22px 0 0;padding:18px 20px 2px;width:100%;max-width:460px;text-align:left;background:#fff;border-radius:22px;
  border:2px solid rgba(11,26,51,.06);box-shadow:0 8px 20px rgba(11,26,51,.06)}
.cf-timeline li{position:relative;display:flex;flex-direction:column;gap:2px;padding:0 0 16px 30px}
.cf-timeline li::before{content:"";position:absolute;left:0;top:3px;width:14px;height:14px;border-radius:50%;background:var(--dot);
  border:3px solid #fff;box-shadow:0 0 0 1px rgba(11,26,51,.04),0 0 8px var(--dot);box-sizing:border-box}
.cf-timeline li:not(:last-child)::after{content:"";position:absolute;left:6px;top:21px;bottom:2px;width:2px;background:rgba(11,26,51,.1)}
.cf-timeline small{font-size:12px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:${SLATE}}
.cf-timeline span{font-size:16.5px;font-weight:600;color:${INK}}
.cf-confetti{position:absolute;inset:0;pointer-events:none;overflow:hidden}
.cf-confetti ~ *{position:relative;z-index:1}
.cf-confetti i{position:absolute;top:-16px;border-radius:3px;animation:cf-drop 2.6s cubic-bezier(.3,.6,.5,1) both}
.cf-enter{animation:cf-rise .35s ease both}
.cf-bob{animation:cf-bob 3s ease-in-out infinite}
.cf-sway{transform-origin:50% 90%;animation:cf-sway 4s ease-in-out infinite}
.cf-hop{animation:cf-hop 1.8s ease-in-out infinite}
.cf-wave{transform-origin:50% 90%;animation:cf-sway 2.4s ease-in-out infinite}
@keyframes cf-fade{from{opacity:0}to{opacity:1}}
@keyframes cf-pop{from{opacity:0;transform:translateY(18px) scale(.97)}to{opacity:1;transform:none}}
@keyframes cf-rise{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}
@keyframes cf-bob{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}
@keyframes cf-sway{0%,100%{transform:rotate(-2.5deg)}50%{transform:rotate(2.5deg)}}
@keyframes cf-hop{0%,100%{transform:translateY(0)}40%{transform:translateY(-10px)}60%{transform:translateY(-10px) rotate(2deg)}}
@keyframes cf-spin{to{transform:rotate(360deg)}}
@keyframes cf-drop{0%{transform:translateY(0) rotate(0);opacity:1}85%{opacity:1}100%{transform:translateY(440px) rotate(var(--spin,540deg));opacity:0}}
@media (max-width:560px){
  .cf-pad{padding:24px 18px 20px}
  .cf-title{font-size:23px}.cf-title.cf-big{font-size:26px}
  .cf-row-actions{flex-direction:column}
  .cf-fan{height:150px}.cf-fan img{width:136px;height:86px}.cf-fan-sq img{width:88px;height:88px}
  .cf-timeline span{font-size:15px}
  .cf-feature{height:150px}.cf-feature-text strong{font-size:19px}
  .cf-tiles{gap:10px}.cf-tile img{height:92px}.cf-tile-text{padding:10px 11px 12px}.cf-tile-text strong{font-size:15px}.cf-tile-text small{font-size:12.5px}
  .cf-pip-mid{width:140px!important}.cf-pip-sm2{width:96px!important}
  .cf-prices{gap:10px;margin-top:26px}.cf-price-pro .cf-price-amount{font-size:32px}.cf-price-amount{font-size:24px}
  .cf-story{width:150px;height:90px}
  .cf-head{margin-right:30px}
  .cf-stage{padding-top:18px}
  .cf-card .cf-pip{width:150px}.cf-stage .cf-pip{width:132px}.cf-card .cf-pip-sm{width:84px}
  .cf-sub{font-size:15.5px}
  .cf-btn{min-height:52px}
  .cf-reason{padding:11px 14px}
  .cf-rays{width:420px;height:420px;margin-left:-210px;bottom:-140px}
  .cf-carousel{padding-top:14px;gap:10px}
  .cf-char-img{width:72px;height:72px;border-radius:20px}
  .cf-story{width:150px;height:90px}
  .cf-char{width:80px}
  .cf-actions{margin-top:14px}
}
/* Laptop-height windows (~800px): tighten so no screen needs scrolling. */
@media (min-width:561px) and (max-height:900px){
  .cf-pad{padding:28px 40px 24px}
  .cf-title{font-size:28px;margin:8px 0 6px}.cf-title.cf-big{font-size:32px}
  .cf-card .cf-pip{width:180px}.cf-stage .cf-pip{width:140px}.cf-card .cf-pip-sm{width:92px}
  .cf-pip-mid{width:140px!important}.cf-pip-sm2{width:84px!important}
  .cf-head{margin-bottom:14px}
  .cf-reasons{gap:8px}.cf-reason{padding:11px 16px}
  .cf-actions{margin-top:18px;gap:6px}
  .cf-try{margin-top:16px;gap:12px}.cf-feature{height:150px}.cf-tile img{height:100px}
  .cf-prices{margin-top:24px}
  .cf-carousel{padding-top:14px;gap:10px}
  .cf-story{width:186px;height:108px}.cf-char{width:96px}.cf-char-img{width:86px;height:86px}
  .cf-reason{padding:10px 16px}.cf-note{margin-top:8px}
  .cf-feature{height:140px}
}
@media (prefers-reduced-motion:reduce){
  .cf-bob,.cf-sway,.cf-hop,.cf-wave,.cf-rays,.cf-confetti i{animation:none}
}
`;
